import { Localized, useLocalization } from '@fluent/react'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import { ButtonGroup, Dialog, FormControl, IconButton, InputLabel, MenuItem, Paper, Select, Stack, TableContainer } from '@mui/material'
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { MailTemplateDialog } from './MailTemplateDialog'
import { AppId } from '../../../app/appTypes'
import { RestoreTemplateDialog } from './RestoreTemplateDialog'
import { LoadingContainer } from '../../../app/LoadingContainer'
import { ErrorContainer } from '../../../app/ErrorContainer'
import { selectSettings } from '../../settings/settingsSlice'
import { enUS, plPL } from '@mui/x-data-grid/locales'
import SyncIcon from '@mui/icons-material/Sync';
import { QuickSearchToolbar } from '../../../app/QuickSearchToolbar'
import { EditIconButton } from '../../../app/components/iconButtons/EditIconButton'
import { RestoreIconButton } from '../../../app/components/iconButtons/RestoreIconButton'
import { DotColorChip } from '../../../app/components/DotColorChip'
import { LoadCompanies } from '../../../app/AppDataLoader'
import { Company, selectAllCompanies } from '../../company/companiesSlice'
import { loadSuperMailTemplates, MailTemplate } from './superMailTemplatesApi'
import { unwrapResult } from '@reduxjs/toolkit'

type DialogState =
    | { type: "none" }
    | { type: "updateTemplate", template: MailTemplate }
    | { type: "restoreTemplate", templateId: AppId }

   


const DialogContentDispatcher = ({ state, setter, onSuccess }: { state: DialogState, setter: Dispatch<SetStateAction<DialogState>>, onSuccess: (template: MailTemplate | string) => void }) => {
    const onCancel = useCallback(() => {
        setter({ type: "none" })
    }, [setter])

    switch (state.type) {
        case "none":
            return null

        case "updateTemplate":
            return <MailTemplateDialog
                template={state.template}
                onSaveSuccess={onSuccess}
                onCancel={onCancel}
            />

        case "restoreTemplate":
            return <RestoreTemplateDialog
            id={state.templateId}
            onSaveSuccess={onSuccess}
            onCancel={onCancel}
            />
    }
}

export const MailTemplates = () => {
    const { locale } = useAppSelector(selectSettings);
    const { l10n } = useLocalization()
    const dispatch = useAppDispatch()
    const [templates, setTemplates] = useState<MailTemplate[]>([])
    const [isLoading, setIsLoading] = useState<boolean>(false)
    const [isError, setIsError] = useState<boolean>(false)
    const [error, setError] = useState(null);

    const companies = useAppSelector(selectAllCompanies)
    const [context, setContext] = useState<string>("all");
    const [companyId, setCompanyId] = useState<AppId | "all">("all");
    const [dialog, setDialog] = useState<DialogState>({ type: "none" })

    const handleContextChange = (e) => setContext(e.target.value);
    const handleCompanyChange = (e) => setCompanyId(e.target.value);

    useEffect(() => {
        setIsLoading(true);
        dispatch(loadSuperMailTemplates())
            .then(unwrapResult)
            .then(setTemplates)
            .catch((error) => {
                console.error(error);
                setIsError(true);
                setError(error);
            })
            .finally(() => setIsLoading(false));
    }, [dispatch])

    const handleEditTemplate = useCallback((template: MailTemplate) => {
        setDialog({
            type: "updateTemplate",
            template
        })
    }, [])

    const handleRestoreTemplate = useCallback((templateId: AppId) => {
        setDialog({
            type: "restoreTemplate",
            templateId
        })
    }, [setDialog])

    const handleRefreshClick = () => {
        setIsLoading(true);
        dispatch(loadSuperMailTemplates({
            context,
            companyId: companyId === "all" ? undefined : companyId,
        }))
            .then(unwrapResult)
            .then(setTemplates)
            .catch((error) => {
                console.error(error);
                setIsError(true);
                setError(error);
            })
            .finally(() => setIsLoading(false));
    }

    const handleTemplateChange = useCallback((template: MailTemplate | string) => {
        if(typeof template === "string") {
            setTemplates((prevTemplates) => prevTemplates.filter((t) => t.id !== template));
        } else {
            setTemplates((prevTemplates) => prevTemplates.map((t) => t.id === template.id ? template : t));
        }

        setDialog({ type: "none" });
    }, []);

    const getCustomLocaleText = () => {
        const defaultEnLocaleText = enUS.components.MuiDataGrid.defaultProps.localeText;

        if (locale === 'pl') {
            return plPL.components.MuiDataGrid.defaultProps.localeText;
        } else {
            return defaultEnLocaleText;
        }
    };

    const columns: GridColDef[] = [
        {
            field: 'type',
            headerName: l10n.getString("mail-type", null, "Typ"),
            flex: 1
        },
        {
            field: 'name',
            headerName: l10n.getString("mail-template-name", null, "Nazwa"),
            flex: 1,
            valueGetter: (_, row) => {
                return l10n.getString(`mail-type-${row.type}`, null, `_${row.type}`)
            }
        },
        {
            field: 'context',
            headerName: l10n.getString("mail-context", null, "Kontekst"),
            flex: 1
        },{
            field: 'company',
            headerName: l10n.getString("company", null, "Firma"),
            flex: 1,
            renderCell: (params) => {
                const company = companies.find((item: Company) => String(item.id) === String(params.row.companyId));
                return company?.name ?? ''
            }
        },
        {
            field: 'isCompanyTemplate',
            headerName: l10n.getString("mail-template-overwritten", null, "Czy nadpisano systemowy szablon?"),
            flex: 1,
            renderCell: (params) => {
                return <DotColorChip l10nId={params.row.companyId ? "yes" : "no"} label={params.row.companyId ? "_Tak" : "_Nie"} color={params.row.companyId ? "success" : "error"} />
            }
        },
        {
            field: 'actions', type: 'actions', width: 350,
            renderCell: (params) => {
                return (
                    <ButtonGroup>
                        { params.row.companyId ?
                            (<RestoreIconButton
                                tooltip={<Localized id="mail-restore-system">Przywróć systemowy</Localized>}
                                onClick={() => handleRestoreTemplate(params.row.id)}
                            />) : null
                        }
                        <EditIconButton
                            tooltip={<Localized id="edit">Edytuj</Localized>}
                            onClick={() => handleEditTemplate(params.row)}
                        />
                    </ButtonGroup>
                )
            }
        }
    ];

    if (isLoading) {
        return <>
            <LoadingContainer />
        </>
    }


    if (isError) {
        return <>
            <ErrorContainer error={error} />
        </>
    }

    return (
        <>
            <LoadCompanies component={<LoadingContainer />}>
                <Stack direction="row" spacing={2} sx={{ marginBottom: 2 }}>
                    <FormControl>
                        <InputLabel id="select-mail-context-label"><Localized id="mail-context">Kontekst</Localized></InputLabel>
                        <Select
                            value={context}
                            onChange={handleContextChange}
                            labelId="select-mail-context-label"
                            label={<Localized id="mail-context">Kontekst</Localized>}
                            sx={{ width: '200px' }}
                        >
                            <MenuItem value="all"><Localized id="all">Wszystkie</Localized></MenuItem>
                            <MenuItem value="DOCUMENT"><Localized id="mail-document">Dokument</Localized></MenuItem>
                            <MenuItem value="SYSTEM">System</MenuItem>
                        </Select>
                    </FormControl>
                    <FormControl>
                        <InputLabel id="select-mail-company-label"><Localized id="company">Firma</Localized></InputLabel>
                        <Select
                            value={companyId}
                            onChange={handleCompanyChange}
                            labelId="select-mail-company-label"
                            label={<Localized id="company">Firma</Localized>}
                            sx={{ width: '240px' }}
                        >
                            <MenuItem value="all"><Localized id="all">Wszystkie</Localized></MenuItem>
                            {companies.map(company => (
                                <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <IconButton onClick={handleRefreshClick}>
                        <SyncIcon />
                    </IconButton>
                </Stack>
                <TableContainer component={Paper}>
                    <DataGrid style={{ minHeight: 100, height: 'calc(100vh - 280px)' }} localeText={getCustomLocaleText()} rows={templates} columns={columns} initialState={{ sorting: { sortModel: [{ field: 'type', sort: 'asc' }] } }} onRowDoubleClick={({ row }) => handleEditTemplate(row)} slots={{ toolbar: QuickSearchToolbar }} />
                </TableContainer>
            </LoadCompanies>
            <Dialog open={dialog.type !== "none"} fullScreen={dialog.type === "restoreTemplate" ? false : true} keepMounted={false}>
                <DialogContentDispatcher state={dialog} setter={setDialog} onSuccess={handleTemplateChange} />
            </Dialog>
        </>
    )
}
